import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { AlertCircle, CheckCircle2, MapPin, Send } from 'lucide-react';

function PendingRequestsReminder({ pendingCount, sending = false, onSend, onReview, feedback, reviewAction, reviewLocationLabel }) {
  const footerRef = useRef(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const sent = feedback?.type === 'success';
  const hasError = feedback?.type === 'error';
  const hasPendingRequests = pendingCount > 0;

  useLayoutEffect(() => {
    const footer = footerRef.current;
    const measure = () => setFooterHeight(footer.getBoundingClientRect().height);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(footer);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [pendingCount, sending, feedback?.type, feedback?.message, reviewAction]);

  return (
    <>
      <Box aria-hidden="true" sx={{ height: `calc(${footerHeight + 24}px + env(safe-area-inset-bottom, 0px))`, flexShrink: 0 }} />
      <Paper
        ref={footerRef}
        component="section"
        aria-label={hasPendingRequests ? 'Enviar solicitudes pendientes' : 'Confirmar revisión de usuarios'}
        variant="outlined"
        sx={(theme) => ({
          position: 'fixed',
          bottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
          left: { xs: 8, sm: 16 },
          right: { xs: 8, sm: 16 },
          zIndex: theme.zIndex.appBar,
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          borderRadius: 2,
          borderColor: !hasPendingRequests || sent ? 'success.main' : 'error.main',
          bgcolor: 'background.paper',
          boxShadow: '0 -2px 16px rgba(0, 0, 0, 0.08)',
        })}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ color: !hasPendingRequests || sent ? 'success.main' : 'error.main', display: 'flex', flexShrink: 0 }}>
              {!hasPendingRequests || sent ? <CheckCircle2 size={22} /> : <AlertCircle size={22} />}
            </Box>
            <Box role={hasError ? 'alert' : 'status'}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {!hasPendingRequests ? 'Revisión de usuarios de la sede' : sent ? 'Aviso enviado' : `${pendingCount} ${pendingCount === 1 ? 'solicitud pendiente' : 'solicitudes pendientes'}`}
                </Typography>
                {hasPendingRequests && onReview && (
                  <Button
                    type="button"
                    size="small"
                    onClick={onReview}
                    aria-haspopup="dialog"
                    sx={{ minWidth: 0, px: 0.5, py: 0, textTransform: 'none', textDecoration: 'underline' }}
                  >
                    Ver cambios
                  </Button>
                )}
                {reviewLocationLabel && <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%',
                  px: 1, py: 0.25, borderRadius: 1, bgcolor: 'action.hover', color: 'text.secondary',
                }}>
                  <MapPin size={13} style={{ flexShrink: 0 }} aria-hidden="true" />
                  <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>{reviewLocationLabel}</Typography>
                </Box>}
              </Stack>
              <Typography variant="body2" sx={{ color: hasError ? 'error.main' : 'text.secondary' }}>
                {hasPendingRequests ? (feedback?.message || 'Sin enviar, los administradores no recibirán aviso.') : 'Confirma después de revisar los datos de los usuarios.'}
              </Typography>
            </Box>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'flex-start' }} sx={{
            flexShrink: 0,
            '& > *': { flex: { xs: 1, md: '0 0 auto' }, minWidth: 0 },
          }}>
            {reviewAction}
            {hasPendingRequests && <Button
            type="button"
            variant="contained"
            color="error"
            onClick={onSend}
            disabled={sending}
            startIcon={<Send size={18} />}
            disableElevation
            sx={{
              flexShrink: 0,
              minHeight: 44,
              px: 2.5,
              py: 1,
              borderRadius: 1.5,
              fontWeight: 700,
              fontSize: '0.8125rem',
              whiteSpace: 'nowrap',
              color: '#fff',
              bgcolor: '#d32f2f',
              animation: sending || sent ? 'none' : 'pending-send-glow 2.8s ease-in-out infinite',
              '@keyframes pending-send-glow': {
                '0%, 100%': { backgroundColor: '#d32f2f', boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.15)' },
                '50%': { backgroundColor: '#b71c1c', boxShadow: '0 0 0 6px rgba(211, 47, 47, 0.16)' },
              },
              '&:hover': { bgcolor: '#b71c1c', animation: 'none' },
              '&.Mui-focusVisible': { outline: '3px solid', outlineColor: 'error.main', outlineOffset: 4, animation: 'none' },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            {sending ? 'Enviando...' : 'TERMINAR Y ENVIAR'}
          </Button>}
          </Stack>
        </Stack>
      </Paper>
    </>
  );
}

export default PendingRequestsReminder;
